<template>
	<div class="catalog-workspace" ref="scrollRef">
		<div
			v-if="!activeCatalog && !isLoadingCatalogs"
			class="card placeholder"
		>
			Select a catalog to view its items.
		</div>
		<div
			v-else-if="isLoadingItems && activeItems.length === 0"
			class="card placeholder"
		>
			Loading items…
		</div>
		<div
			v-else-if="activeItems.length === 0 && !activeTemplate"
			class="card placeholder"
		>
			No template configured for {{ activeCatalog?.name }}.
		</div>
		<div v-else class="catalog-shell">
			<div class="catalog-list">
				<CatalogItemList
					:catalog="activeCatalog"
					:items="activeItems"
					:isLoading="isLoadingItems"
					:error="itemsError"
					:active-item-id="selectedItemId"
					@select-item="handleSelectItem"
					@edit-item="handleEditItem"
					@delete-item="handleDeleteItem"
					@create-item="startCreate"
				/>
			</div>
			<div v-if="hasCatalogItems" class="catalog-form-panel">
				<div class="form-header">
					<h2 class="form-title">
						{{ formTitle }}
					</h2>
					<div v-if="activeTemplate && canDelete" class="action-wrapper">
						<button
							@click.stop="toggleSubmenu"
							title="More options"
							class="more-options-btn"
						>
							<BsThreeDots class="icon-more" />
						</button>
						<div v-if="isSubmenuOpen" class="submenu">
							<button
								@click="showDeleteConfirm"
								class="submenu-btn delete"
								type="button"
								:disabled="isDeletingItem"
							>
								Delete
							</button>
						</div>
					</div>
				</div>
				<div v-if="activeTemplate">
					<CatalogItemForm
						ref="catalogItemFormRef"
						v-model="formModel"
						:template="catalogFormTemplate"
						:supplier-mobile-status="supplierMobileStatusLabel"
					>
						<template v-if="isSuppliersCatalog && selectedItemId" #section-end-right-to-work-uk>
							<RightToWorkHistory :key="selectedItemId" :supplier-id="selectedItemId" />
						</template>
					</CatalogItemForm>
					<CatalogItemForm
						v-if="isClientsCatalog"
						class="client-buildings-form"
						:template="clientBuildingsTemplate"
						:model-value="{ clientBuildings: selectedBuildingIds }"
						:disabled="!canManageClientBuildings"
						@update:model-value="handleBuildingMultiSelectChange($event.clientBuildings)"
					/>
				<SupplierAccessPanel
					v-if="isSuppliersCatalog"
					:supplier-id="selectedItemId"
					:disabled="!selectedItemId"
					:attachments="formModel.attachments"
					:analysis-enabled="!!selectedItemId"
					:analyzing-attachment-id="rightToWorkAnalyzingAttachmentId"
					@update:attachments="handleSupplierAttachmentsChange"
					@analyze-attachment="analyzeRightToWorkAttachment"
				/>
				</div>
				<div v-else class="card placeholder">
					No template available for {{ activeCatalog?.name }}.
				</div>
			</div>
		</div>

		<RightToWorkAiReviewModal
			v-if="rightToWorkReviewResult"
			:result="rightToWorkReviewResult"
			:current-values="formModel"
			@cancel="rightToWorkReviewResult = null"
			@apply="applyRightToWorkExtraction"
		/>

		<div v-if="showDeleteModal" class="modal-overlay" @click="cancelDelete">
			<div class="modal-content" @click.stop>
				<h3 class="modal-title">Delete Item</h3>
				<p class="modal-message">
					Are you sure you want to delete this {{ singularCatalogName }}? This
					action cannot be undone.
				</p>
				<div class="modal-actions">
					<button class="modal-btn cancel" @click="cancelDelete">
						Cancel
					</button>
					<button
						class="modal-btn confirm"
						@click="confirmDelete"
						:disabled="isDeletingItem"
					>
						{{ isDeletingItem ? 'Deleting...' : 'Delete' }}
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import {
	computed,
	ref,
	watch,
	getCurrentInstance,
	nextTick,
	onBeforeUnmount,
	toRaw,
	onActivated,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useCatalogStore } from '@/stores/catalog';
import { sameRecords, useLiveRefresh } from '@/composables/useLiveRefresh';
import { apiErrorMessage } from '@/utils/apiError';
import { useSupplierPortalInsightsStore } from '@/stores/supplierPortalInsights';
import { useSupplierPortalAdminStore } from '@/stores/supplierPortalAdmin';
import CatalogItemForm from '@/components/catalog/CatalogItemForm.vue';
import CatalogItemList from '@/components/catalog/CatalogItemList.vue';
import RightToWorkHistory from '@/components/catalog/RightToWorkHistory.vue';
import SupplierAccessPanel from '@/components/catalog/SupplierAccessPanel.vue';
import RightToWorkAiReviewModal from '@/components/catalog/RightToWorkAiReviewModal.vue';
import {
	getCatalogTemplate,
	buildModelFromTemplate,
	cloneCatalogTemplate,
	normalizeCatalogKey,
} from '@/constants/catalogTemplates';
import { BsThreeDots } from '@kalimahapps/vue-icons';
import { normalizeInvoicePaymentTerms } from '@/utils/invoicePaymentTerms';
import { isUnfinishedEmail } from '@/utils/email';

const props = defineProps({
	catalogId: {
		type: [String, Number],
		default: null,
	},
});

const router = useRouter();
const route = useRoute();
const catalogStore = useCatalogStore();
const insightsStore = useSupplierPortalInsightsStore();
const supplierPortalAdminStore = useSupplierPortalAdminStore();
const instance = getCurrentInstance();
const toast = instance?.proxy?.$toast;
const logError = instance?.proxy?.$logError;
const scrollRef = ref(null);
const catalogItemFormRef = ref(null);
const catalogKey = (catalog) => normalizeCatalogKey(catalog);

const normalizeRelationId = (value) => {
	if (value === null || value === undefined) return null;
	if (typeof value === 'number') return value;
	if (typeof value === 'string') {
		const parsed = Number(value);
		return Number.isNaN(parsed) ? value : parsed;
	}
	return value?.id ?? value?.attributes?.id ?? null;
};

const getRelationTargetIds = (relation) => {
	if (!relation) return [];
	if (Array.isArray(relation)) {
		return relation.map(normalizeRelationId).filter(Boolean);
	}
	if (relation.data) {
		const data = relation.data;
		if (Array.isArray(data)) {
			return data.map(normalizeRelationId).filter(Boolean);
		}
		const id = normalizeRelationId(data);
		return id ? [id] : [];
	}
	const id = normalizeRelationId(relation);
	return id ? [id] : [];
};

const relationHasId = (relationIds, targetId) =>
	relationIds.some((id) => String(id) === String(targetId));

const applyRelationDefaults = (model, sourceItem) => {
	if (isClientsBuildingsCatalog.value) {
		return {
			...model,
			clients: getRelationTargetIds(sourceItem?.clients) || [],
		};
	}
	return model;
};

const selectedItemId = ref(null);
const isCreatingNew = ref(false);
const formModel = ref({});
const isSubmenuOpen = ref(false);
const showDeleteModal = ref(false);
const lastSuccessfulSnapshot = ref(null);
const suppressItemSync = ref(false);
const buildingSelectionValue = ref(null);
const buildingRelationLoading = ref({});
let submenuClickHandler = null;

const AUTOSAVE_DELAY = 500;
const autosaveTimer = ref(null);
const autosaveState = ref('idle');
const autosaveError = ref(null);
const isSyncingFormModel = ref(false);
const formVersion = ref(0);
const supplierMobileSyncing = ref(false);
const lastSupplierMobileSyncKey = ref('');
const rightToWorkAnalyzingAttachmentId = ref(null);
const rightToWorkReviewResult = ref(null);
let rightToWorkAnalysisToken = 0;
const rightToWorkReviewSupplierId = ref(null);

const normalizePhoneInput = (value) =>
	String(value || '')
		.trim()
		.replace(/[\s()-]/g, '');

const isValidMobilePhone = (value) => /^\+\d{6,15}$/.test(normalizePhoneInput(value));

const resolvedCatalogId = computed(
	() => props.catalogId ?? route.params.catalogId ?? null,
);
const catalogList = computed(() => catalogStore.catalogList);
const activeCatalogId = computed(() => catalogStore.activeCatalogId);
const activeCatalog = computed(() => catalogStore.activeCatalog);
const activeItems = computed(() => catalogStore.itemsForActiveCatalog);
const storedActiveItemId = computed(() => {
	if (!activeCatalogId.value) return null;
	return catalogStore.activeItemIds?.[activeCatalogId.value] ?? null;
});

watch(
	() => [route.name, resolvedCatalogId.value, catalogStore.catalogList.length],
	([currentRouteName, catalogId]) => {
		if (currentRouteName !== 'catalog-view') {
			return;
		}
		if (catalogId) {
			catalogStore.setActiveCatalog(catalogId);
			return;
		}
		const fallback = catalogStore.catalogList[0];
		if (fallback) {
			router.replace({
				name: 'catalog-view',
				params: { catalogId: fallback.id },
			});
		}
	},
	{ immediate: true },
);

const currentCatalogKey = computed(() => catalogKey(activeCatalog.value));
const isClientsCatalog = computed(() => currentCatalogKey.value === 'clients');
const isClientsBuildingsCatalog = computed(
	() => currentCatalogKey.value === 'clients-buildings',
);
const isServicesCatalog = computed(() => currentCatalogKey.value === 'services');
const isSuppliersCatalog = computed(() => currentCatalogKey.value === 'suppliers');
const clientsCatalogEntity = computed(
	() => catalogList.value.find((catalog) => catalogKey(catalog) === 'clients') || null,
);
const buildingsCatalogEntity = computed(
	() =>
		catalogList.value.find((catalog) => catalogKey(catalog) === 'clients-buildings') ||
		null,
);
const clientsCatalogIdRef = computed(() => clientsCatalogEntity.value?.id ?? null);
const buildingsCatalogIdRef = computed(
	() => buildingsCatalogEntity.value?.id ?? null,
);
const itemsByCatalogId = computed(() => catalogStore.itemsByCatalogId || {});
const clientCatalogItems = computed(() =>
	clientsCatalogIdRef.value
		? itemsByCatalogId.value?.[clientsCatalogIdRef.value] || []
		: [],
);
const buildingCatalogItems = computed(() =>
	buildingsCatalogIdRef.value
		? itemsByCatalogId.value?.[buildingsCatalogIdRef.value] || []
		: [],
);
const supplierBuildingOptions = computed(() => {
	const options = buildingCatalogItems.value.map((building) => ({
		id: building.id,
		name: building.name || 'Untitled building',
	}));
	const optionIds = new Set(options.map((option) => String(option.id)));
	const selectedIds = Array.isArray(formModel.value?.buildings)
		? formModel.value.buildings
			.map((id) => normalizeRelationId(id))
			.filter((id) => id !== null && id !== undefined && id !== '')
		: [];
	const missingOptions = [];

	selectedIds.forEach((id) => {
		const normalizedId = String(id);
		if (optionIds.has(normalizedId)) return;
		optionIds.add(normalizedId);
		missingOptions.push({
			id,
			name: `Missing building (ID: ${id})`,
		});
	});

	return [...options, ...missingOptions];
});
const buildingClientOptions = computed(() =>
	clientCatalogItems.value.map((client) => ({
		id: client.id,
		name: client.name || 'Untitled client',
	})),
);
const serviceGroupOptions = computed(() => catalogStore.serviceGroupOptions || []);
const hasCatalogItems = computed(() => activeItems.value.length > 0);
const isLoadingCatalogs = computed(() => catalogStore.loadingCatalogs);
const isLoadingItems = computed(() => catalogStore.loadingItems);
const itemsError = computed(() => catalogStore.itemsError);

const baseTemplate = computed(() =>
	activeCatalog.value ? getCatalogTemplate(activeCatalog.value) : null,
);
const activeTemplate = computed(() => {
	const template = baseTemplate.value;
	if (!template) return null;
	const needsClone = isClientsBuildingsCatalog.value || isServicesCatalog.value;
	const workingTemplate = needsClone ? cloneCatalogTemplate(template) : template;

	if (isClientsBuildingsCatalog.value) {
		const firstRow = workingTemplate?.rows?.[0];
		if (firstRow) {
			const relationField = {
				key: 'clients',
				label: 'Clients',
				type: 'client-select',
				options: buildingClientOptions.value,
				multiple: true,
				placeholder: 'Select clients',
			};
			const existingIndex = firstRow.fields.findIndex((field) => field.key === 'clients');
			if (existingIndex >= 0) {
				firstRow.fields.splice(existingIndex, 1, relationField);
			} else {
				firstRow.fields.push(relationField);
			}
		}
	}

	if (isServicesCatalog.value && Array.isArray(workingTemplate?.rows)) {
		workingTemplate.rows.forEach((row) => {
			row.fields.forEach((field) => {
				if (field.key === 'services_groups' && field.type === 'service-groups') {
					field.options = serviceGroupOptions.value;
				}
			});
		});
	}

	return workingTemplate;
});
const emailFieldKeys = computed(() => (activeTemplate.value?.rows || [])
	.flatMap((row) => row.fields || [])
	.filter((field) => field.type === 'email')
	.map((field) => field.key));
const catalogFormTemplate = computed(() => {
	const template = activeTemplate.value;
	if (!template) return null;
	if (!isSuppliersCatalog.value) return template;
	const clone = cloneCatalogTemplate(template);
	clone.rows = (clone.rows || [])
		.map((row) => ({
			...row,
			fields: (row.fields || [])
				.filter((field) => field.key !== 'attachments')
				.map((field) => {
					if (field.key === 'buildings' && field.type === 'client-select') {
						return {
							...field,
							options: supplierBuildingOptions.value,
						};
					}
					return field;
				}),
		}))
		.filter((row) => row.fields.length > 0);
	return clone;
});
const isSavingItem = computed(() => catalogStore.savingItem);
const isDeletingItem = computed(() => catalogStore.deletingItem);
const canDelete = computed(() => !!selectedItemId.value);
const selectedItem = computed(
	() =>
		activeItems.value.find((item) => item.id === selectedItemId.value) || null,
);
const selectedSupplierPortalEntry = computed(() => {
	if (!isSuppliersCatalog.value || !selectedItemId.value) return null;
	return (
		(insightsStore.suppliers || []).find(
			(entry) => String(entry?.supplier?.id || '') === String(selectedItemId.value),
		) || null
	);
});
const supplierPhoneForMobileAccess = computed(() =>
	normalizePhoneInput(formModel.value?.telephone || ''),
);
const supplierPhoneCanEnableMobile = computed(() =>
	isValidMobilePhone(supplierPhoneForMobileAccess.value),
);
const supplierMobileNeedsSync = computed(() => {
	if (!isSuppliersCatalog.value || !selectedItemId.value || !supplierPhoneCanEnableMobile.value) {
		return false;
	}
	const portal = selectedSupplierPortalEntry.value?.portal || {};
	if (!portal?.hasProfile) return true;
	if (portal.mobileEnabled === false) return true;
	return normalizePhoneInput(portal.phoneE164 || '') !== supplierPhoneForMobileAccess.value;
});
const supplierMobileStatusLabel = computed(() => {
	if (!isSuppliersCatalog.value || !selectedItemId.value) return '';
	if (
		supplierPhoneCanEnableMobile.value &&
		(supplierMobileSyncing.value || (selectedSupplierPortalEntry.value && supplierMobileNeedsSync.value))
	) {
		return supplierMobileSyncing.value || autosaveState.value === 'saving' || autosaveTimer.value
			? 'Turning on...'
			: 'Off';
	}
	const portal = selectedSupplierPortalEntry.value?.portal || {};
	if (portal?.hasProfile || typeof portal?.mobileEnabled === 'boolean') {
		return portal.mobileEnabled !== false && Boolean(portal.phoneE164) ? 'On' : 'Off';
	}

	const status = String(portal?.status || '').trim();
	if (!status) {
		return insightsStore.loading ? '' : 'Off';
	}
	if (status === 'active') return 'On';
	return 'Off';
});
const selectedItemClientId = computed(() => selectedItem.value?.id ?? null);
const canManageClientBuildings = computed(
	() => isClientsCatalog.value && !!selectedItemClientId.value,
);
const selectedClientBuildings = computed(() => {
	if (!isClientsCatalog.value || !selectedItemClientId.value) return [];
	return buildingCatalogItems.value.filter(
		(building) =>
			relationHasId(getRelationTargetIds(building.clients), selectedItemClientId.value),
	);
});
const availableBuildingOptions = computed(() => {
	if (!isClientsCatalog.value || !selectedItemClientId.value) return [];
	return buildingCatalogItems.value
		.filter((building) => {
			const assignedIds = getRelationTargetIds(building.clients);
			return !relationHasId(assignedIds, selectedItemClientId.value);
		})
		.map((building) => ({
			id: building.id,
			name: building.name || 'Untitled building',
		}));
});
const selectedBuildingIds = computed(() => {
	return selectedClientBuildings.value.map((building) => building.id);
});
const allBuildingOptionsForClient = computed(() => {
	if (!isClientsCatalog.value || !selectedItemClientId.value) return [];
	return buildingCatalogItems.value.map((building) => ({
		id: building.id,
		name: building.name || 'Untitled building',
	}));
});
const clientBuildingsTemplate = computed(() => ({
	key: 'client-building-links',
	rows: [{
		id: 'client-buildings',
		fields: [{
			key: 'clientBuildings',
			label: 'Client’s buildings',
			type: 'client-select',
			options: allBuildingOptionsForClient.value,
			multiple: true,
			stackedChips: true,
			placeholder: 'Search buildings to link',
		}],
	}],
}));
const singularCatalogName = computed(() => {
	const name = activeCatalog.value?.name?.trim();
	if (!name) return 'item';
	if (name.endsWith('s') && name.length > 1) {
		return name.slice(0, -1);
	}
	return name;
});
const formTitle = computed(() => {
	const itemName = (formModel.value?.name || '').trim();
	if (isCreatingNew.value) {
		return itemName || `New ${singularCatalogName.value}`;
	}
	return itemName || `Edit ${singularCatalogName.value}`;
});

const clearAutosaveTimer = () => {
	if (autosaveTimer.value) {
		clearTimeout(autosaveTimer.value);
		autosaveTimer.value = null;
	}
};

const persistSelectedItem = (itemId) => {
	if (!activeCatalogId.value) return;
	const setter = catalogStore.setActiveCatalogItemId;
	if (typeof setter === 'function') {
		setter(activeCatalogId.value, itemId || null);
	}
};

const canAutosave = () => {
	return (
		!!selectedItem.value &&
		!!activeCatalogId.value &&
		!!activeTemplate.value &&
		!isDeletingItem.value
	);
};

const scheduleAutosave = () => {
	if (!canAutosave()) return;
	clearAutosaveTimer();
	autosaveError.value = null;
	if (autosaveState.value === 'error') {
		autosaveState.value = 'idle';
	}
	autosaveTimer.value = setTimeout(() => {
		autosaveTimer.value = null;
		runAutosave();
	}, AUTOSAVE_DELAY);
};

const runAutosave = async () => {
	if (!canAutosave() || autosaveState.value === 'saving') return;
	const versionAtRequest = formVersion.value;
	autosaveState.value = 'saving';
	autosaveError.value = null;
	suppressItemSync.value = true;

	try {
		const modelToSave = isClientsCatalog.value
			? {
				...formModel.value,
				terms: normalizeInvoicePaymentTerms(formModel.value?.terms),
			}
			: { ...formModel.value };
		// The server rejects a half-typed email and the whole card would roll back, so it
		// stays on screen and the last saved address is sent until it is complete.
		const unfinishedEmails = {};
		emailFieldKeys.value.forEach((key) => {
			if (isUnfinishedEmail(modelToSave[key])) {
				unfinishedEmails[key] = modelToSave[key];
				modelToSave[key] = lastSuccessfulSnapshot.value?.[key] ?? null;
			} else if (typeof modelToSave[key] === 'string') {
				modelToSave[key] = modelToSave[key].trim();
			}
		});
		const hasUnfinishedEmail = Object.keys(unfinishedEmails).length > 0;
		if (hasUnfinishedEmail && JSON.stringify(modelToSave) === JSON.stringify(lastSuccessfulSnapshot.value)) {
			autosaveState.value = 'idle';
			return;
		}
		const saved = await catalogStore.saveCatalogItem(
			activeCatalogId.value,
			modelToSave,
			selectedItem.value,
		);
		if (saved) {
			selectedItemId.value = saved.id;
			if (formVersion.value === versionAtRequest) {
				syncFormModel(saved);
				if (hasUnfinishedEmail) {
					// Still inside syncFormModel's sync window, so this does not schedule another save.
					formModel.value = { ...formModel.value, ...unfinishedEmails };
				}
			} else {
				syncAttachmentFieldsFromSaved(saved);
			}
			autosaveState.value = 'idle';
			await syncSupplierMobileAccessFromSavedItem(saved);
		} else {
			autosaveState.value = 'idle';
		}
	} catch (error) {
		autosaveState.value = 'error';
		autosaveError.value = error;
		restoreSnapshot();
		const reason = apiErrorMessage(error) || 'Please try again.';
		toast?.error?.(`Auto-save failed. ${reason} Previous saved values were restored.`);
		logError?.(error, 'autoSave', { notify: false });
	} finally {
		suppressItemSync.value = false;
	}
};

const syncSupplierMobileAccessFromSavedItem = async (savedItem) => {
	if (!isSuppliersCatalog.value || !savedItem?.id) return;
	const phoneE164 = normalizePhoneInput(savedItem.telephone || formModel.value?.telephone || '');
	if (!isValidMobilePhone(phoneE164)) return;

	const portal = selectedSupplierPortalEntry.value?.portal || {};
	if (
		portal?.hasProfile &&
		portal.mobileEnabled !== false &&
		normalizePhoneInput(portal.phoneE164 || '') === phoneE164
	) {
		return;
	}

	const syncKey = `${savedItem.id}:${phoneE164}`;
	if (supplierMobileSyncing.value && lastSupplierMobileSyncKey.value === syncKey) return;

	supplierMobileSyncing.value = true;
	lastSupplierMobileSyncKey.value = syncKey;
	try {
		await supplierPortalAdminStore.updateSupplierMobile({
			supplierId: savedItem.id,
			phoneE164,
			mobileEnabled: true,
		});
		await insightsStore.fetchOverview();
	} catch (error) {
		toast?.error?.('Supplier saved, but mobile access was not turned on.');
		logError?.(error, 'syncSupplierMobileAccessFromSavedItem');
	} finally {
		supplierMobileSyncing.value = false;
	}
};

const handleSelectItem = (item) => {
	if (!item) return;
	selectedItemId.value = item.id;
	persistSelectedItem(item.id);
	isCreatingNew.value = false;
	syncFormModel(item);
};

const focusNameField = async () => {
	await nextTick();
	if (typeof catalogItemFormRef.value?.focusField === 'function') {
		await catalogItemFormRef.value.focusField('name');
	}
};

const startCreate = async () => {
	if (!activeCatalogId.value) return;
	if (!activeTemplate.value) {
		toast?.error?.('Cannot create a catalog item without a template.');
		return;
	}
	if (isSavingItem.value) return;

	try {
		isCreatingNew.value = true;
		const blankModel = buildModelFromTemplate(activeTemplate.value);
		const created = await catalogStore.saveCatalogItem(
			activeCatalogId.value,
			blankModel,
		);
		if (created) {
			selectedItemId.value = created.id;
			persistSelectedItem(created.id);
			syncFormModel(created);
			await focusNameField();
		}
	} catch (error) {
		isCreatingNew.value = false;
		toast?.error?.('Failed to create catalog item.');
		logError?.(error, 'startCreate');
	}
};

const handleEditItem = (item) => {
	if (!item) return;
	handleSelectItem(item);
};

const ensureSelectedItem = () => {
	const items = activeItems.value;
	if (!items.length) {
		selectedItemId.value = null;
		persistSelectedItem(null);
		if (!activeTemplate.value) {
			formModel.value = {};
			return;
		}
		formModel.value = buildModelFromTemplate(activeTemplate.value);
		return;
	}

	const existing = items.find((item) => item.id === selectedItemId.value);
	if (existing) {
		syncFormModel(existing);
		return;
	}

	const fallback = items[0];
	selectedItemId.value = fallback.id;
	persistSelectedItem(fallback.id);
	isCreatingNew.value = false;
	syncFormModel(fallback);
};

const showDeleteConfirm = () => {
	isSubmenuOpen.value = false;
	showDeleteModal.value = true;
};

const cancelDelete = () => {
	showDeleteModal.value = false;
};

const confirmDelete = async () => {
	const item = selectedItem.value;
	if (!activeCatalogId.value || !item) {
		showDeleteModal.value = false;
		return;
	}
	try {
		await catalogStore.deleteCatalogItem(activeCatalogId.value, item);
		toast?.success?.('Catalog item deleted.');
		showDeleteModal.value = false;
		if (selectedItemId.value === item.id) {
			selectedItemId.value = null;
			persistSelectedItem(null);
			isCreatingNew.value = false;
		}
		ensureSelectedItem();
	} catch (error) {
		toast?.error?.('Failed to delete catalog item.');
		logError?.(error, 'handleDelete');
	}
};

const handleDelete = async (targetItem = null) => {
	const item = targetItem || selectedItem.value;
	if (!activeCatalogId.value || !item) return;
	isSubmenuOpen.value = false;
	try {
		await catalogStore.deleteCatalogItem(activeCatalogId.value, item);
		toast?.success?.('Catalog item deleted.');
		if (selectedItemId.value === item.id) {
			selectedItemId.value = null;
			persistSelectedItem(null);
			isCreatingNew.value = false;
		}
		ensureSelectedItem();
	} catch (error) {
		toast?.error?.('Failed to delete catalog item.');
		logError?.(error, 'handleDelete');
	}
};

const handleDeleteItem = async (item) => {
	if (!item) return;
	selectedItemId.value = item.id;
	persistSelectedItem(item.id);
	isCreatingNew.value = false;
	syncFormModel(item);
	await handleDelete(item);
};

const setBuildingRelationLoading = (buildingId, state) => {
	if (!buildingId) return;
	if (state) {
		buildingRelationLoading.value = {
			...buildingRelationLoading.value,
			[buildingId]: true,
		};
	} else {
		const next = { ...buildingRelationLoading.value };
		delete next[buildingId];
		buildingRelationLoading.value = next;
	}
};

const updateBuildingRelation = async (buildingId, clientId, { remove = false } = {}) => {
	if (!buildingsCatalogIdRef.value) {
		toast?.error?.("Clients' Buildings catalog unavailable.");
		return;
	}
	const building = buildingCatalogItems.value.find((item) => item.id === buildingId);
	if (!building) {
		toast?.error?.('Building not found.');
		return;
	}
	const currentClients = getRelationTargetIds(building.clients);
	const normalizedClientId = normalizeRelationId(clientId);
	let nextClients = currentClients;
	if (normalizedClientId !== null && normalizedClientId !== undefined) {
		if (remove) {
			nextClients = currentClients.filter(
				(id) => String(id) !== String(normalizedClientId),
			);
		} else if (!relationHasId(currentClients, normalizedClientId)) {
			nextClients = [...currentClients, normalizedClientId];
		}
	}
	const templateName = buildingsCatalogEntity.value?.name || "Clients' Buildings";
	const templateSource = buildingsCatalogEntity.value || templateName;
	const template = getCatalogTemplate(templateSource);
	const model = buildModelFromTemplate(template, building);
	model.clients = nextClients;
	setBuildingRelationLoading(buildingId, true);
	try {
		await catalogStore.saveCatalogItem(
			buildingsCatalogIdRef.value,
			model,
			building,
		);
	} catch (error) {
		toast?.error?.('Failed to update building relation.');
		logError?.(error, 'updateBuildingRelation');
	} finally {
		setBuildingRelationLoading(buildingId, false);
	}
};

const handleBuildingPickerChange = async (value) => {
	buildingSelectionValue.value = value;
	if (!value) {
		buildingSelectionValue.value = null;
		return;
	}
	if (!selectedItemClientId.value) {
		toast?.info?.('Save the client before linking buildings.');
		buildingSelectionValue.value = null;
		return;
	}
	await updateBuildingRelation(value, selectedItemClientId.value);
	buildingSelectionValue.value = null;
};

const handleBuildingMultiSelectChange = async (newIds) => {
	if (!selectedItemClientId.value) {
		toast?.info?.('Save the client before linking buildings.');
		return;
	}
	const currentIds = selectedBuildingIds.value;
	const newIdsSet = new Set(newIds.map((id) => String(id)));
	const currentIdsSet = new Set(currentIds.map((id) => String(id)));

	// Find buildings to add (in newIds but not in currentIds)
	const toAdd = newIds.filter((id) => !currentIdsSet.has(String(id)));
	// Find buildings to remove (in currentIds but not in newIds)
	const toRemove = currentIds.filter((id) => !newIdsSet.has(String(id)));

	// Process additions
	for (const buildingId of toAdd) {
		await updateBuildingRelation(buildingId, selectedItemClientId.value);
	}

	// Process removals
	for (const buildingId of toRemove) {
		await updateBuildingRelation(buildingId, selectedItemClientId.value, { remove: true });
	}
};

const handleRemoveBuilding = async (buildingId) => {
	await updateBuildingRelation(buildingId, selectedItemClientId.value, { remove: true });
};

const handleSupplierAttachmentsChange = (attachments) => {
	formModel.value = {
		...formModel.value,
		attachments: Array.isArray(attachments) ? attachments : [],
	};
};

const analyzeRightToWorkAttachment = async (attachment) => {
	if (!isSuppliersCatalog.value || !selectedItem.value) return;
	if (!attachment?.id) {
		toast?.info?.('Wait for the attachment to finish saving, then try again.');
		return;
	}

	const analysisToken = ++rightToWorkAnalysisToken;
	const supplierId = selectedItemId.value;
	rightToWorkAnalyzingAttachmentId.value = attachment.id;
	rightToWorkReviewResult.value = null;
	try {
		const itemId = selectedItem.value.documentId || selectedItem.value.id;
		const result = await catalogStore.extractRightToWork(itemId, attachment.id);
		if (analysisToken !== rightToWorkAnalysisToken || supplierId !== selectedItemId.value) return;
		if (!result?.extraction) {
			throw new Error('No Right to Work details were returned');
		}
		rightToWorkReviewSupplierId.value = supplierId;
		rightToWorkReviewResult.value = result;
	} catch (error) {
		if (analysisToken !== rightToWorkAnalysisToken) return;
		const message = error?.response?.data?.error?.message
			|| error?.message
			|| 'Right to Work AI could not read this document.';
		toast?.error?.(message);
		logError?.(error, 'CatalogView::analyzeRightToWorkAttachment');
	} finally {
		if (analysisToken === rightToWorkAnalysisToken) rightToWorkAnalyzingAttachmentId.value = null;
	}
};

const applyRightToWorkExtraction = (patch = {}) => {
	if (rightToWorkReviewSupplierId.value !== selectedItemId.value) return;
	const previous = formModel.value || {};
	const next = { ...previous, ...patch };
	const appliedListType = patch.rightToWorkListType;

	if (appliedListType === 'list_a') {
		next.rightToWorkExpiryDate = '';
	}

	formModel.value = next;
	rightToWorkReviewResult.value = null;
	toast?.success?.('Selected Right to Work details applied. They will be auto-saved.');
};

const syncAttachmentFieldsFromSaved = (savedItem) => {
	const template = activeTemplate.value;
	if (!template || !savedItem) return;
	const attachmentKeys = (template.rows || [])
		.flatMap((row) => row.fields || [])
		.filter((field) => field?.type === 'attachments' && field?.key)
		.map((field) => field.key);
	if (!attachmentKeys.length) return;

	const nextModel = { ...formModel.value };
	let hasChanges = false;
	attachmentKeys.forEach((key) => {
		if (savedItem[key] === undefined) return;
		nextModel[key] = savedItem[key];
		hasChanges = true;
	});
	if (!hasChanges) return;

	isSyncingFormModel.value = true;
	formModel.value = nextModel;
	nextTick(() => {
		isSyncingFormModel.value = false;
	});
};

const syncFormModel = (sourceItem = null) => {
	const template = activeTemplate.value;
	clearAutosaveTimer();
	autosaveError.value = null;
	isSyncingFormModel.value = true;

	if (!template) {
		formModel.value = {};
		autosaveState.value = 'idle';
		nextTick(() => {
			isSyncingFormModel.value = false;
		});
		return;
	}

	const currentItem = sourceItem || selectedItem.value;
	const baseModel = currentItem
		? buildModelFromTemplate(template, currentItem)
		: buildModelFromTemplate(template);
	const nextModel = applyRelationDefaults(baseModel, currentItem);
	if (isClientsCatalog.value) {
		nextModel.terms = normalizeInvoicePaymentTerms(nextModel.terms);
	}
	formModel.value = nextModel;
	captureSnapshot();
	autosaveState.value = 'idle';
	nextTick(() => {
		isSyncingFormModel.value = false;
	});
};

const toggleSubmenu = () => {
	isSubmenuOpen.value = !isSubmenuOpen.value;
};

const closeSubmenu = () => {
	isSubmenuOpen.value = false;
};

const removeSubmenuListener = () => {
	if (!submenuClickHandler) return;
	document.removeEventListener('click', submenuClickHandler);
	submenuClickHandler = null;
};

const attachSubmenuListener = () => {
	removeSubmenuListener();
	submenuClickHandler = (event) => {
		if (!event.target.closest('.action-wrapper')) {
			closeSubmenu();
		}
	};
	document.addEventListener('click', submenuClickHandler);
};

watch(
	[activeCatalogId, activeTemplate],
	() => {
		isCreatingNew.value = false;
		if (storedActiveItemId.value) {
			selectedItemId.value = storedActiveItemId.value;
		}
		ensureSelectedItem();
	},
	{ immediate: true },
);

watch([selectedItemId, activeCatalogId], () => {
	rightToWorkAnalysisToken++;
	rightToWorkReviewResult.value = null;
	rightToWorkReviewSupplierId.value = null;
	rightToWorkAnalyzingAttachmentId.value = null;
}, { flush: 'sync' });

watch(activeItems, () => {
	if (suppressItemSync.value) return;
	ensureSelectedItem();
});

watch(
	[() => selectedItemId.value, () => isClientsCatalog.value],
	() => {
		buildingSelectionValue.value = null;
	},
);

onActivated(() => {
	if (resolvedCatalogId.value) {
		catalogStore.setActiveCatalog(resolvedCatalogId.value);
	} else if (catalogStore.catalogList.length) {
		catalogStore.setActiveCatalog(catalogStore.catalogList[0].id);
	}
	selectedItemId.value = storedActiveItemId.value;
	ensureSelectedItem();
});

watch(
	formModel,
	() => {
		if (isSyncingFormModel.value) return;
		formVersion.value += 1;
		if (!canAutosave()) return;
		scheduleAutosave();
	},
	{ deep: true },
);

watch(
	() => catalogStore.error,
	(value) => {
		if (value) {
			toast?.error?.(`Failed to load catalogs: ${value}`);
			logError?.(new Error(value), 'fetchCatalogs');
		}
	},
);

watch(itemsError, (value) => {
	if (value) {
		toast?.error?.(`Failed to load catalog items: ${value}`);
		logError?.(new Error(value), 'fetchCatalogItems');
	}
});

watch(isSubmenuOpen, (isOpen) => {
	if (isOpen) {
		nextTick(() => {
			attachSubmenuListener();
		});
	} else {
		removeSubmenuListener();
	}
});

onBeforeUnmount(() => {
	rightToWorkAnalysisToken++;
	clearAutosaveTimer();
	removeSubmenuListener();
});

function captureSnapshot() {
	lastSuccessfulSnapshot.value = cloneFormModel(formModel.value);
}

function restoreSnapshot() {
	if (!lastSuccessfulSnapshot.value) return;
	isSyncingFormModel.value = true;
	formModel.value = cloneFormModel(lastSuccessfulSnapshot.value);
	nextTick(() => {
		isSyncingFormModel.value = false;
	});
}

function cloneFormModel(source) {
	const raw = deepUnref(source);
	if (typeof structuredClone === 'function') {
		return structuredClone(raw);
	}
	return deepCloneFallback(raw);
}

function deepUnref(value) {
	if (Array.isArray(value)) {
		return value.map((item) => deepUnref(item));
	}
	if (value && typeof value === 'object') {
		if (
			value instanceof File ||
			value instanceof Blob ||
			value instanceof Date
		) {
			return value;
		}
		const raw = toRaw(value);
		const result = Array.isArray(raw) ? [] : {};
		for (const key in raw) {
			result[key] = deepUnref(raw[key]);
		}
		return result;
	}
	return value;
}

function deepCloneFallback(value) {
	if (Array.isArray(value)) {
		return value.map((item) => deepCloneFallback(item));
	}
	if (value && typeof value === 'object') {
		if (value instanceof File || value instanceof Blob) {
			return value;
		}
		if (value instanceof Date) {
			return new Date(value.getTime());
		}
		const result = Array.isArray(value) ? [] : {};
		Object.keys(value).forEach((key) => {
			result[key] = deepCloneFallback(value[key]);
		});
		return result;
	}
	return value;
}

// Items added or changed on another device show up here, and in the journals' lists, without
// reloading. The catalogs are read again quietly, and only replaced while the open item has no
// save on its way and nothing is being created, deleted or reviewed.
const isCatalogBusy = () =>
	isLoadingCatalogs.value ||
	isLoadingItems.value ||
	Boolean(autosaveTimer.value) ||
	autosaveState.value === 'saving' ||
	isCreatingNew.value ||
	isDeletingItem.value ||
	supplierMobileSyncing.value ||
	isSubmenuOpen.value ||
	showDeleteModal.value ||
	suppressItemSync.value ||
	Boolean(rightToWorkAnalyzingAttachmentId.value || rightToWorkReviewResult.value);

const refreshCatalogQuietly = async ({ canApply }) => {
	if (!catalogStore.catalogIds.length || isCatalogBusy()) return;
	const fresh = await catalogStore.peekCatalogItems();
	if (!canApply() || isCatalogBusy()) return;
	if (sameRecords(catalogStore.itemsByCatalogId, fresh)) return;
	catalogStore.itemsByCatalogId = fresh;
};

useLiveRefresh(refreshCatalogQuietly, { isBusy: isCatalogBusy });
</script>

<style lang="sass" scoped>
@use '@/assets/styles/variables.sass' as *

.catalog-workspace
	min-height: 0
	height: 100%
	display: flex
	flex-direction: column
	padding: 24px
	background: var(--noc-background)
	overflow: auto

.card.placeholder
	flex: 1
	border-radius: 4px
	border: 1px dashed var(--border-color)
	display: flex
	align-items: center
	justify-content: center
	color: var(--text-color-secondary)
	font-size: 1rem
	background: var(--noc-background)

.catalog-shell
	flex: 1
	display: flex
	gap: 24px
	min-height: 0

.catalog-list
	flex: 1
	display: flex
	flex-direction: column
	gap: 16px
	min-width: 0

.catalog-form-panel
	flex: 1
	border-radius: var(--border-radius-secondary, 10px)
	border: 1px solid var(--border-color)
	padding: 24px
	background: var(--noc-background)
	box-shadow: var(--box-shadow)
	display: flex
	flex-direction: column
	gap: 24px
	max-width: none
	overflow-y: auto
	min-height: 0

.client-buildings-form
	margin-top: -1px

.form-header
	display: flex
	justify-content: space-between
	align-items: center
	gap: 12px
	flex-wrap: wrap

.form-title
	margin: 0
	font-family: var(--font-family-display, 'SF Pro Display', sans-serif)
	font-size: 1.5rem
	font-weight: 600
	color: var(--text-color)

.action-wrapper
	position: relative

.more-options-btn
	background: none
	border: none
	cursor: pointer
	color: var(--text-color-secondary)
	font-size: 1.1rem
	padding: 4px
	border-radius: 4px
	display: flex
	align-items: center
	line-height: 1
	outline: none
	box-shadow: none
	appearance: none

	&:focus,
	&:focus-visible
		outline: none
		box-shadow: none
		border: none

	&:hover
		background-color: var(--noc-grey-light)

.submenu
	position: absolute
	right: 0
	top: 100%
	margin-top: 4px
	background-color: var(--noc-background)
	border: 1px solid var(--border-color)
	border-radius: 4px
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15)
	z-index: 10
	min-width: 150px
	padding: 4px 0

	.submenu-btn
		display: block
		width: 100%
		padding: 8px 16px
		background: none
		border: none
		color: var(--text-color)
		text-align: left
		font-size: 0.9rem
		cursor: pointer
		white-space: nowrap
		font-weight: 500

		&.delete
			color: var(--noc-red)
			&:hover
				background-color: var(--noc-red-10)
				color: var(--noc-red)

		&:hover:not(.delete)
			background-color: var(--noc-blue-10)
			color: var(--noc-blue)

		&:disabled
			opacity: 1

.modal-overlay
	position: fixed
	top: 0
	left: 0
	right: 0
	bottom: 0
	background-color: rgba(0, 0, 0, 0.5)
	display: flex
	align-items: center
	justify-content: center
	z-index: 1000

.modal-content
	background: var(--noc-background)
	border-radius: 8px
	border: 1px solid var(--border-color)
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2)
	padding: 24px
	min-width: 320px
	max-width: 90%
	display: flex
	flex-direction: column
	gap: 16px

.modal-title
	margin: 0
	font-size: 1.25rem
	font-weight: 600
	color: var(--text-color)

.modal-message
	margin: 0
	font-size: 0.95rem
	color: var(--text-color-secondary)
	line-height: 1.5

.modal-actions
	display: flex
	gap: 12px
	justify-content: flex-end
	margin-top: 8px

.modal-btn
	padding: 8px 20px
	border-radius: 4px
	border: none
	font-weight: 600
	cursor: pointer
	transition: opacity 0.15s ease, background 0.15s ease
	font-size: 0.8rem

	&.cancel
		background: transparent
		color: var(--text-color-secondary)
		border: 1px solid var(--border-color)

		&:hover
			background: var(--noc-background-secondary)
			color: var(--text-color)

	&.confirm
		background: var(--noc-red)
		color: #fff

		&:hover:not(:disabled)
			background: var(--noc-red-80)

		&:disabled
			opacity: 1

@media (max-width: 900px)
	.catalog-shell
		flex-direction: column

	.catalog-form-panel
		width: 100%
		max-width: 100%
</style>
